// ============================================================================
// KLINISOL CRO - User Management Page (REDESIGNED)
// Pure Tailwind CSS - Matches Dashboard/Navbar design system
// FDA 21 CFR Part 11 Compliant - Super Admin Only
// ============================================================================

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import UserForm from '../components/UserForm';
import PasswordResetModal from '../components/PasswordResetModal';
import { 
    Users, 
    UserCheck, 
    UserX, 
    Award,
    UserPlus,
    Search,
    Edit,
    Key,
    Ban,
    CheckCircle,
    Trash2,
    Home,
    PowerOff,
    ChevronLeft,
    ChevronRight,
    AlertTriangle
} from 'lucide-react';

const UserManagement = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    
    // Modals
    const [showUserForm, setShowUserForm] = useState(false);
    const [showPasswordReset, setShowPasswordReset] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    
    // Filters
    const [filters, setFilters] = useState({
        search: '',
        role_id: '',
        is_active: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    // ========================================================================
    // FETCH DATA
    // ========================================================================
    
    useEffect(() => {
        if (user?.role !== 'Super Admin') {
            navigate('/dashboard');
            return;
        }
        
        fetchUsers();
        fetchRoles();
        fetchStats();
    }, [user, navigate, filters, pagination.page]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...filters
            });

            const response = await api.get(`/users?${params}`);
            
            if (response.data.success) {
                setUsers(response.data.data.users);
                setPagination(response.data.data.pagination);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
            alert('Failed to load users');
        } finally {
            setLoading(false);
        }
    };

    const fetchRoles = async () => {
        try {
            const response = await api.get('/users/roles/list');
            if (response.data.success) {
                setRoles(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching roles:', error);
        }
    };

    const fetchStats = async () => {
        try {
            const response = await api.get('/users/stats/overview');
            if (response.data.success) {
                setStats(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    // ========================================================================
    // HANDLERS
    // ========================================================================

    const handleLogout = async () => {
        await logout();
        navigate('/login');
    };

    const handleAddUser = () => {
        setSelectedUser(null);
        setShowUserForm(true);
    };

    const handleEditUser = (userId) => {
        const userToEdit = users.find(u => u.id === userId);
        setSelectedUser(userToEdit);
        setShowUserForm(true);
    };

    const handlePasswordReset = (userId) => {
        const userToReset = users.find(u => u.id === userId);
        setSelectedUser(userToReset);
        setShowPasswordReset(true);
    };

    const handleDeactivateUser = async (userId) => {
        const userToDeactivate = users.find(u => u.id === userId);
        
        if (!window.confirm(`Are you sure you want to deactivate ${userToDeactivate.first_name} ${userToDeactivate.last_name}?`)) {
            return;
        }

        try {
            const response = await api.patch(`/users/${userId}/deactivate`);
            if (response.data.success) {
                alert('User deactivated successfully');
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            console.error('Error deactivating user:', error);
            alert(error.response?.data?.message || 'Failed to deactivate user');
        }
    };

    const handleReactivateUser = async (userId) => {
        try {
            const response = await api.patch(`/users/${userId}/reactivate`);
            if (response.data.success) {
                alert('User reactivated successfully');
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            console.error('Error reactivating user:', error);
            alert(error.response?.data?.message || 'Failed to reactivate user');
        }
    };

    const handleDeleteUser = async (userId) => {
        const userToDelete = users.find(u => u.id === userId);
        
        if (!window.confirm(`⚠️ PERMANENTLY DELETE ${userToDelete.first_name} ${userToDelete.last_name}?\n\nThis action CANNOT be undone!`)) {
            return;
        }

        const confirmText = window.prompt('Type "DELETE" to confirm permanent deletion:');
        if (confirmText !== 'DELETE') {
            alert('Deletion cancelled');
            return;
        }

        try {
            const response = await api.delete(`/users/${userId}`);
            if (response.data.success) {
                alert('User deleted permanently');
                fetchUsers();
                fetchStats();
            }
        } catch (error) {
            console.error('Error deleting user:', error);
            alert(error.response?.data?.message || 'Failed to delete user');
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleUserFormClose = (refreshNeeded) => {
        setShowUserForm(false);
        setSelectedUser(null);
        if (refreshNeeded) {
            fetchUsers();
            fetchStats();
        }
    };

    const handlePasswordResetClose = () => {
        setShowPasswordReset(false);
        setSelectedUser(null);
    };

    // ========================================================================
    // RENDER
    // ========================================================================

    return (
        <div className="min-h-screen bg-[#f8fafd]">
            {/* Header */}
            <header className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white shadow-lg sticky top-0 z-50">
                <div className="max-w-7xl mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold">
                                KLIN<span className="text-[#E91E63]">i</span>SOL
                            </h1>
                            <p className="text-sm opacity-90">User Management Portal</p>
                        </div>
                        <div className="flex items-center gap-3">
                            <div className="text-right mr-3">
                                <p className="font-semibold text-sm">{user?.firstName} {user?.lastName}</p>
                                <p className="text-xs opacity-90">{user?.role}</p>
                            </div>
                            <button 
                                onClick={() => navigate('/dashboard')}
                                className="flex items-center gap-2 px-4 py-2 bg-white/15 hover:bg-white/25 backdrop-blur-sm rounded-lg transition-all font-medium text-sm"
                            >
                                <Home className="w-4 h-4" />
                                Dashboard
                            </button>
                            <button 
                                onClick={handleLogout}
                                className="w-11 h-11 rounded-full bg-gradient-to-br from-[#475569] to-[#E91E63] hover:from-[#334155] hover:to-[#C2185B] transition-all shadow-lg hover:shadow-xl hover:scale-105 flex items-center justify-center group"
                                title="Logout"
                            >
                                <PowerOff className="w-5 h-5 text-white group-hover:rotate-12 transition-transform" />
                            </button>
                        </div>
                    </div>
                </div>
            </header>

            <main className="max-w-7xl mx-auto px-6 py-8">
                {/* Statistics Cards */}
                {stats && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                        <div className="bg-gradient-to-br from-[#475569] to-[#334155] text-white rounded-xl shadow-lg p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold mb-1">{stats.overview.total_users}</div>
                                    <div className="text-sm opacity-90">Total Users</div>
                                </div>
                                <Users className="w-12 h-12 opacity-80" />
                            </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white rounded-xl shadow-lg p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold mb-1">{stats.overview.active_users}</div>
                                    <div className="text-sm opacity-90">Active Users</div>
                                </div>
                                <UserCheck className="w-12 h-12 opacity-80" />
                            </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-gray-500 to-gray-600 text-white rounded-xl shadow-lg p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold mb-1">{stats.overview.inactive_users}</div>
                                    <div className="text-sm opacity-90">Inactive Users</div>
                                </div>
                                <UserX className="w-12 h-12 opacity-80" />
                            </div>
                        </div>
                        
                        <div className="bg-gradient-to-br from-[#E91E63] to-[#C2185B] text-white rounded-xl shadow-lg p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <div className="text-3xl font-bold mb-1">{stats.overview.roles_used}</div>
                                    <div className="text-sm opacity-90">Roles Used</div>
                                </div>
                                <Award className="w-12 h-12 opacity-80" />
                            </div>
                        </div>
                    </div>
                )}

                {/* Actions Bar */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
                    <div className="flex flex-col lg:flex-row gap-4">
                        <button 
                            onClick={handleAddUser}
                            className="flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-[#475569] to-[#334155] text-white font-semibold rounded-lg hover:from-[#334155] hover:to-[#1e293b] transition-all shadow-lg"
                        >
                            <UserPlus className="w-5 h-5" />
                            Add New User
                        </button>

                        <div className="flex flex-col md:flex-row gap-4 flex-1">
                            <div className="flex-1 relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                                <input
                                    type="text"
                                    name="search"
                                    placeholder="Search users..."
                                    value={filters.search}
                                    onChange={handleFilterChange}
                                    className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all"
                                />
                            </div>
                            
                            <select
                                name="role_id"
                                value={filters.role_id}
                                onChange={handleFilterChange}
                                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all bg-white"
                            >
                                <option value="">All Roles</option>
                                {roles.map(role => (
                                    <option key={role.id} value={role.id}>{role.role_name}</option>
                                ))}
                            </select>

                            <select
                                name="is_active"
                                value={filters.is_active}
                                onChange={handleFilterChange}
                                className="px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#475569] focus:ring-4 focus:ring-[#475569]/10 outline-none transition-all bg-white"
                            >
                                <option value="">All Status</option>
                                <option value="true">Active</option>
                                <option value="false">Inactive</option>
                            </select>
                        </div>
                    </div>
                </div>

                {/* Users Table */}
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    {loading ? (
                        <div className="text-center py-12 text-gray-600">Loading users...</div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gradient-to-r from-gray-50 to-gray-100 border-b-2 border-gray-200">
                                    <tr>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">ID</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Name</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Email</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Role</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Department</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Status</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Last Login</th>
                                        <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-200">
                                    {users.length === 0 ? (
                                        <tr>
                                            <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                                                No users found
                                            </td>
                                        </tr>
                                    ) : (
                                        users.map(u => (
                                            <tr 
                                                key={u.id} 
                                                className={`hover:bg-gray-50 transition-colors ${!u.is_active ? 'bg-gray-50/50 opacity-60' : ''}`}
                                            >
                                                <td className="px-4 py-4 text-sm text-gray-900 font-medium">{u.id}</td>
                                                <td className="px-4 py-4">
                                                    <div className="text-sm font-semibold text-gray-900">
                                                        {u.first_name} {u.last_name}
                                                    </div>
                                                    {u.must_change_password && (
                                                        <div className="flex items-center gap-1 mt-1">
                                                            <AlertTriangle className="w-3 h-3 text-yellow-600" />
                                                            <span className="text-xs text-yellow-600 font-medium">Must Change Password</span>
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600">{u.email}</td>
                                                <td className="px-4 py-4">
                                                    <span className="inline-block px-3 py-1 bg-[#475569] text-white text-xs font-semibold rounded-full">
                                                        {u.role_name}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600">{u.department || '-'}</td>
                                                <td className="px-4 py-4">
                                                    <span className={`inline-block px-3 py-1 text-xs font-semibold rounded-full ${
                                                        u.is_active 
                                                            ? 'bg-green-100 text-green-700' 
                                                            : 'bg-red-100 text-red-700'
                                                    }`}>
                                                        {u.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-4 text-sm text-gray-600">
                                                    {u.last_login 
                                                        ? new Date(u.last_login).toLocaleDateString()
                                                        : 'Never'
                                                    }
                                                </td>
                                                <td className="px-4 py-4">
                                                    <div className="flex items-center gap-2">
                                                        <button 
                                                            onClick={() => handleEditUser(u.id)}
                                                            className="p-2 text-[#475569] hover:bg-gray-100 rounded-lg transition-all"
                                                            title="Edit User"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </button>
                                                        <button 
                                                            onClick={() => handlePasswordReset(u.id)}
                                                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-all"
                                                            title="Reset Password"
                                                        >
                                                            <Key className="w-4 h-4" />
                                                        </button>
                                                        {u.is_active ? (
                                                            <button 
                                                                onClick={() => handleDeactivateUser(u.id)}
                                                                className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                                                title="Deactivate User"
                                                                disabled={u.id === user?.id}
                                                            >
                                                                <Ban className="w-4 h-4" />
                                                            </button>
                                                        ) : (
                                                            <button 
                                                                onClick={() => handleReactivateUser(u.id)}
                                                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-all"
                                                                title="Reactivate User"
                                                            >
                                                                <CheckCircle className="w-4 h-4" />
                                                            </button>
                                                        )}
                                                        <button 
                                                            onClick={() => handleDeleteUser(u.id)}
                                                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                                                            title="Delete User Permanently"
                                                            disabled={u.id === user?.id}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Pagination */}
                {pagination.pages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6">
                        <button 
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                            disabled={pagination.page === 1}
                            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            <ChevronLeft className="w-5 h-5" />
                            Previous
                        </button>
                        <span className="text-sm text-gray-600 font-medium">
                            Page {pagination.page} of {pagination.pages} ({pagination.total} total)
                        </span>
                        <button 
                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                            disabled={pagination.page === pagination.pages}
                            className="flex items-center gap-2 px-4 py-2 bg-white border-2 border-gray-200 text-gray-700 font-medium rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            Next
                            <ChevronRight className="w-5 h-5" />
                        </button>
                    </div>
                )}
            </main>

            {/* Footer */}
            <footer className="bg-gradient-to-r from-[#475569] via-[#E91E63] to-[#475569] text-white py-6 px-6 mt-12">
                <div className="max-w-7xl mx-auto text-center">
                    <h3 className="text-xl font-bold mb-2">
                        KLINiSOL
                    </h3>
                    <p className="text-sm opacity-90">&copy; 2025 KLINiSOL CRO. All rights reserved.</p>
                </div>
            </footer>

            {/* Modals */}
            {showUserForm && (
                <UserForm
                    user={selectedUser}
                    roles={roles}
                    onClose={handleUserFormClose}
                />
            )}

            {showPasswordReset && (
                <PasswordResetModal
                    user={selectedUser}
                    onClose={handlePasswordResetClose}
                />
            )}
        </div>
    );
};

export default UserManagement;